import { Box, Paper, Skeleton } from '@mui/material';

import { DECISION_COLUMN_WIDTH } from './RuteroResultCard';
import { RESULT_MEDIA_HEIGHT, RESULT_MEDIA_WIDTH } from './ResultMedia';

interface Props {
  /**
   * La geometría de lo que se está esperando. Un spinner centrado no dice
   * nada; un esqueleto con la forma real del arquetipo hace que la lista
   * aparezca en su lugar en vez de saltar cuando llega.
   */
  archetype: 'product' | 'schedule' | 'table';
  /** Cuántas filas dibujar. Por defecto, lo que entra en una pantalla. */
  count?: number;
}

const DEFAULT_COUNT = 4;

export function ResultsSkeleton({ archetype, count = DEFAULT_COUNT }: Props) {
  const rows = Array.from({ length: count }, (_, index) => index);

  const cards = (withMedia: boolean) => (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {rows.map((row) => (
        <Paper
          key={row}
          variant="outlined"
          sx={{
            borderRadius: 1,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg: withMedia
                ? `${RESULT_MEDIA_WIDTH}px minmax(0, 1fr) ${DECISION_COLUMN_WIDTH}px`
                : `minmax(0, 1fr) ${DECISION_COLUMN_WIDTH}px`,
            },
            gap: 1.75,
            p: '12px 14px',
          }}
        >
          {withMedia && (
            <Skeleton
              variant="rounded"
              width={RESULT_MEDIA_WIDTH}
              height={RESULT_MEDIA_HEIGHT}
              sx={{ display: { xs: 'none', lg: 'block' } }}
            />
          )}
          <Box minWidth={0}>
            <Skeleton width="35%" height={18} />
            <Skeleton width="70%" height={26} />
            <Skeleton width="55%" height={18} />
          </Box>
          {/* En mobile la columna de decisión es la fila de cierre: el
              esqueleto la dibuja donde va a estar, no la esconde. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'row', lg: 'column' },
              alignItems: { xs: 'center', lg: 'stretch' },
              justifyContent: { xs: 'space-between', lg: 'center' },
              gap: 1,
              borderTop: { xs: 1, lg: 0 },
              borderColor: 'divider',
              pt: { xs: 1, lg: 0 },
            }}
          >
            <Skeleton width={110} height={28} />
            <Skeleton width={92} height={28} sx={{ ml: { lg: 'auto' } }} />
          </Box>
        </Paper>
      ))}
    </Box>
  );

  // La tabla de eSIMs solo existe de `sm` para arriba: abajo son cards, y el
  // esqueleto tiene que reservar ESA geometría o la pantalla salta al cargar.
  if (archetype === 'table') {
    return (
      <>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Paper variant="outlined" sx={{ borderRadius: 1, p: 1.75 }}>
            {rows.map((row) => (
              <Skeleton key={row} height={36} sx={{ mb: row === count - 1 ? 0 : 1 }} />
            ))}
          </Paper>
        </Box>
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>{cards(false)}</Box>
      </>
    );
  }

  return cards(archetype === 'product');
}
